
import React, { useEffect, useState } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCalendarAlt, FaPhoneAlt, FaBuilding, FaEye } from 'react-icons/fa';
import CallAPI from '../../../api/CallAPI';
import MeetingAPI from '../../../api/MeetingAPI';
import DateUtil from '../../../util/Util';
import Const from '../../../util/Constants';
import { reactLocalStorage } from 'reactjs-localstorage';

const getNextDays = (count = 7) => {
  const today = new Date();
  today.setDate(today.getDate() - 2);
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return {
      full: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      weekday: date.toLocaleDateString('en-IN', { weekday: 'short' }),
      isToday: date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0],
    };
  });
};

const MiniCalendar = (props) => {
  const [dates] = useState(getNextDays(7));
  const [calls, setCalls] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    dates.find((d) => d.isToday)?.full || dates[2].full
  );
  const model = reactLocalStorage.getObject(Const.Model);

  const list = {
    filters: [
      props.filters,
      {
        field: 'date_time',
        condition: 'between',
        value: `${DateUtil.dateToServerDateTime(dates[0].full)}~${DateUtil.dateToServerDateTime(dates[6].full)}`,
      },
      {
        field: 'complete',
        condition: 'in',
        value: '',
      },
    ],
    pagination: {
      page: 1,
      currentPage: 1,
      perPage: 1000,
    },
  };

  const leadData = async () => {
    setLoading(true);
    try {
      CallAPI.getList(list, (res) => setCalls(res.data || []), console.error);
      MeetingAPI.getList(list, (res) => setMeetings(res.data || []), console.error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  useEffect(() => {
    leadData();
  }, [props.filters]);

  const combinedActivities = [
    ...calls.map((call) => ({
      id: call.id,
      type: 'call',
      data: call,
      title: model.call.model_name || 'Call',
      dateTime: call.date_time,
      name: `${call.lead_first} ${call.lead_midle || ''} ${call.lead_last}`.trim(),
      phone: call.lead_phone?.replace(/~+/g, '') || '',
    })),
    ...meetings.map((meeting) => ({
      id: meeting.id,
      data: meeting,
      type: 'visit',
      title: model.meeting.model_name || 'Site Visit',
      dateTime: meeting.date_time,
      name: `${meeting.lead_first} ${meeting.lead_midle || ''} ${meeting.lead_last}`.trim(),
      phone: meeting.lead_phone?.replace(/~+/g, '') || '',
    })),
  ];

  const activitiesForSelectedDate = combinedActivities.filter((act) =>
    act.dateTime?.startsWith(selectedDate)
  );
  const sortedActivities = activitiesForSelectedDate.sort(
    (a, b) => new Date(a.dateTime) - new Date(b.dateTime)
  );

  const onPreview = (data) => {
    const modalData = data.data;
    if (!modalData?.model_no || !modalData?.model_id) return;
    if (modalData.model_no === Const.Lead) props.navigate(`/lead/preview/${modalData.model_id}`);
    else if (modalData.model_no === Const.Contact) props.navigate(`/contact/preview/${modalData.model_id}`);
  };

  return (
    <Card className="border-0 shadow-sm h-100 rounded-4">
      <Card.Body className="d-flex flex-column p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">My Schedule</h6>
          <FaCalendarAlt className="text-primary" size={18} />
        </div>

        <div className="d-flex gap-2 mb-3 overflow-auto">
          {dates.map((date) => {
            const isActive = selectedDate === date.full;
            const isToday = date.isToday;
            return (
              <div
                key={date.full}
                onClick={() => setSelectedDate(date.full)}
                className="text-center rounded-4 px-3 py-2"
                style={{
                  minWidth: 64,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  background: isActive
                    ? 'linear-gradient(135deg, #007bff, #3399ff)'
                    : isToday
                    ? '#f1f3f5'
                    : '#f8f9fa',
                  color: isActive ? '#fff' : isToday ? '#007bff' : '#6c757d',
                  border: isToday ? '1px solid #007bff' : '1px solid #dee2e6',
                  boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.1)' : '',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <div>{date.weekday}</div>
                <div>{date.label}</div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="fw-semibold text-muted">
            {new Date(selectedDate).toLocaleDateString('en-IN', {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
          </small>
          <span
            className="badge rounded-pill px-3"
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: sortedActivities.length === 0 ? '#fff' : '#0d6efd',
              background:
                sortedActivities.length === 0
                  ? 'linear-gradient(135deg, #868e96, #adb5bd)'
                  : 'linear-gradient(135deg, #e3f2fd, #e0f7ff)',
            }}
          >
            {sortedActivities.length} Activities
          </span>
        </div>

        <div className="flex-grow-1 overflow-auto pe-1" style={{ maxHeight: 260 }}>
          {loading ? (
            <div className="text-center text-muted py-4">Loading...</div>
          ) : sortedActivities.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {sortedActivities.map((act, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between align-items-center px-3 py-2 rounded-3"
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderLeft: `4px solid ${act.type === 'call' ? '#198754' : '#0d6efd'}`,
                    transition: 'background 0.3s',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    {act.type === 'call' ? (
                      <FaPhoneAlt className="text-success" size={14} style={{marginRight: '12px'}}/>
                    ) : (
                      <FaBuilding className="text-primary" size={14} style={{marginRight: '12px'}}/>
                    )}

                    <div className="d-flex flex-column small">
                      <div className="fw-semibold" style={{fontWeight: 700}}>{act.name || 'No Name'}</div>
                    <div className="text-muted d-flex align-items-center gap-1">
                      <span>{act.phone}</span>
                      <span className="badge bg-secondary text-white px-2 py-1 rounded-pill" style={{ fontSize: '0.65rem', marginLeft: '10px' }}>
                        {DateUtil.serverStringToDisplayTime(act.dateTime)}
                      </span>
                    </div>

                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <span
                      className={`badge text-white px-2 py-1 rounded-pill ${
                        act.type === 'call' ? 'bg-success' : 'bg-primary'
                      }`}
                      style={{ fontSize: '0.65rem', marginRight: '12px' }}
                    >
                      {act.title}
                    </span>
                    <OverlayTrigger placement="top" overlay={<Tooltip>View</Tooltip>}>
                      <button
                        className="btn btn-sm btn-light border rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 28, height: 28 }}
                        onClick={() => onPreview(act)}
                      >
                        <FaEye size={12} className="text-secondary" />
                      </button>
                    </OverlayTrigger>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted small text-center py-4">No activities scheduled</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default MiniCalendar;
